import * as types from '@/store/centralizedIssues/actionTypes';
import {
  CLEAR_CENTRALIZED_ISSUES,
  SET_ISSUES_LIST_LOADING,
  SET_CENTRALIZED_ISSUES,
  SET_CENTRALIZED_ISSUES_LIST_OPTIONS,
  SET_ISSUE_DETAILS_FOR_CENTRALIZED_VIEW,
  CLEAR_ISSUE_DETAILS_FOR_CENTRALIZED_VIEW,
  SET_OPEN_DETAILED_ISSUES_POPUP,
  SET_CENTRALIZED_ISSUES_FOR_DASHBOARD,
  CLEAR_ISSUES_PROGRESS,
  SET_ISSUES_RUNNING_INTERVAL,
  SET_ISSUES_REF_ID,
  SET_ISSUES_PROGRESS,
  CLEAR_ISSUES_REF_ID,
  SET_DASHBOARD_ISSUES_REF_ID,
  SET_DASHBOARD_ISSUES_PROGRESS,
  SET_PROJECT_ISSUES_PROGRESS,
  SET_PROJECT_ISSUES_REF_ID,
  CLEAR_DASHBOARD_ISSUES_REF_ID,
  CLEAR_DASHBOARD_ISSUES_PROGRESS,
  CLEAR_PROJECT_ISSUES_REF_ID,
  CLEAR_PROJECT_ISSUES_PROGRESS,
  SET_DASHBOARD_ISSUES_LIST_LOADING,
  SET_PROJECT_ISSUES_LIST_LOADING,
  SET_DASHBOARD_ISSUES_RUNNING_INTERVAL,
  SET_PROJECT_ISSUES_RUNNING_INTERVAL,
} from '@/store/centralizedIssues/mutationTypes';
// eslint-disable-next-line import/no-cycle
import { changeIssueSuppressStatus, loadCentralizedIssues } from '@/services/repository';
import {
  SET_CENTRALIZED_ISSUES_PAGINATION_STATE,
} from '@/store/pagination/mutationTypes';
import {
  CLEAR_CENTRALIZED_ISSUES_LIST_OPTIONS,
} from '@/store/centralizedIssues/actionTypes';
import {
  SET_RETRY_COUNTER,
} from '@/store/configuration/mutationTypes';

export default {
  async [types.SET_OPEN_DETAILED_ISSUES_POPUP]({ commit }, value) {
    commit(SET_OPEN_DETAILED_ISSUES_POPUP, value);
  },
  /**
   * Suppress (with details) or restore one issue. Same endpoint as bulk, one id.
   * payload: { issueId, suppressedValue, suppression? }
   */
  async [types.TOGGLE_SUPPRESS_STATUS_FOR_ISSUE](_, payload) {
    return changeIssueSuppressStatus({
      issueIds: [payload.issueId],
      suppressedValue: payload.suppressedValue,
      ...(payload.suppressedValue && payload.suppression ? { suppression: payload.suppression } : {}),
    });
  },
  /**
   * payload: { issueIds } or { selectAll, filter, excludeIds }, plus suppressedValue and,
   * when suppressing, suppression: { type, reason, reasonOther, justification, expiresAt }.
   */
  async [types.BULK_TOGGLE_SUPPRESS_STATUS_FOR_ISSUES](_, payload) {
    return changeIssueSuppressStatus(payload);
  },
  async [types.CLEAR_DETAILED_ISSUES_FOR_CENTRALIZED_VIEW]({ commit }) {
    commit(CLEAR_ISSUE_DETAILS_FOR_CENTRALIZED_VIEW);
  },
  async [types.SET_DETAILED_ISSUES_FOR_CENTRALIZED_VIEW]({ commit }, issue) {
    commit(SET_ISSUE_DETAILS_FOR_CENTRALIZED_VIEW, issue);
  },
  async [types.CLEAR_CENTRALIZED_ISSUES_LIST_OPTIONS]({ commit }) {
    commit(CLEAR_CENTRALIZED_ISSUES_LIST_OPTIONS);
  },
  async [types.CHANGE_CENTRALIZES_ISSUES_LIST_OPTIONS]({ commit, dispatch, getters }, options) {
    let search = '';
    const toggles = {};

    if (options.searchUpdated) {
      search = options.search;
    } else {
      search = options.search === undefined ? getters.centralizedIssuesListOptions.search : options.search;
    }
    if (options.hideSuppress === undefined) {
      toggles.hideSuppress = getters.centralizedIssuesListOptions.toggles.hideSuppress;
    } else {
      toggles.hideSuppress = options.hideSuppress;
    }
    if (options.onlyCVEs === undefined) {
      toggles.onlyCVEs = getters.centralizedIssuesListOptions.toggles.onlyCVEs;
    } else {
      toggles.onlyCVEs = options.onlyCVEs;
    }
    if (options.hideFixed === undefined) {
      toggles.hideFixed = getters.centralizedIssuesListOptions.toggles.hideFixed;
    } else {
      toggles.hideFixed = options.hideFixed;
    }
    if (options.specificSearch === undefined) {
      toggles.specificSearch = getters.centralizedIssuesListOptions.toggles.specificSearch;
    } else {
      toggles.specificSearch = options.specificSearch;
    }
    commit(SET_CENTRALIZED_ISSUES_LIST_OPTIONS, {
      issueTypes: options.issueTypes || getters.centralizedIssuesListOptions.issueTypes,
      sort: options.sort || getters.centralizedIssuesListOptions.sort,
      limit: options.limit || getters.centralizedIssuesListOptions.limit,
      page: options.page || getters.centralizedIssuesListOptions.page,
      toggles,
      search,
    });

    const payload = {
      sort: options.sort,
      limit: options.limit,
      page: options.page,
      search: options.search,
      hideSuppress: toggles.hideSuppress,
      onlyCVEs: toggles.onlyCVEs,
      hideFixed: toggles.hideFixed,
      turnOnLoading: options.turnOnLoading,
      specificSearch: options.specificSearch,
      refId: options?.refId || '',
      issueTypes: options.issueTypes || getters.centralizedIssuesListOptions.issueTypes || [],
    };

    if (options.domain === undefined) {
      if (options.projectId?.length) {
        payload.projectId = options.projectId;
      } else {
        payload.domain = getters.domainFilter;
      }
    } else {
      payload.domain = options.domain;
    }

    await dispatch(types.LOAD_CENTRALIZED_ISSUES, payload);
  },
  async [types.LOAD_CENTRALIZED_ISSUES]({ commit, getters }, payload) {
    let result = {};
    // Set when a poll response arrives for a refId that is no longer active (filter/page
    // changed mid-load). Such responses must be ignored so stale data can't overwrite the
    // current list or clear the in-flight refId.
    let stalePoll = false;
    try {
      const toggles = {};

      if (payload.turnOnLoading) {
        if (payload.forDashboard) {
          if (!payload?.refId) {
            await commit(CLEAR_DASHBOARD_ISSUES_PROGRESS);
          }
          await commit(SET_DASHBOARD_ISSUES_LIST_LOADING, true);
        } else if (payload.projectId) {
          if (!payload?.refId) {
            await commit(CLEAR_PROJECT_ISSUES_PROGRESS);
          }
          await commit(SET_PROJECT_ISSUES_LIST_LOADING, true);
        } else {
          if (!payload?.refId) {
            await commit(CLEAR_ISSUES_PROGRESS);
          }
          await commit(SET_ISSUES_LIST_LOADING, true);
        }
      }

      if (payload.hideSuppress === undefined) {
        toggles.hideSuppress = getters.centralizedIssuesListOptions.toggles.hideSuppress;
      } else {
        toggles.hideSuppress = payload.hideSuppress;
      }
      if (payload.onlyCVEs === undefined) {
        toggles.onlyCVEs = getters.centralizedIssuesListOptions.toggles.onlyCVEs;
      } else {
        toggles.onlyCVEs = payload.onlyCVEs;
      }
      if (payload.hideFixed === undefined) {
        toggles.hideFixed = getters.centralizedIssuesListOptions.toggles.hideFixed;
      } else {
        toggles.hideFixed = payload.hideFixed;
      }
      if (payload.specificSearch === undefined) {
        toggles.specificSearch = getters.centralizedIssuesListOptions.toggles.specificSearch;
      } else {
        toggles.specificSearch = payload.specificSearch;
      }
      const payloadFilter = {};
      if (payload.domain === undefined) {
        if (payload.projectId?.length) {
          payloadFilter.projectId = payload.projectId;
        } else {
          payloadFilter.domain = getters.domainFilter;
        }
      } else {
        payloadFilter.domain = payload.domain;
      }
      payloadFilter.issueTypes = payload.issueTypes || getters.centralizedIssuesListOptions.issueTypes || [];

      if (payload.forDashboard) {
        await commit(SET_DASHBOARD_ISSUES_RUNNING_INTERVAL, true);
      } else if (payload.projectId) {
        await commit(SET_PROJECT_ISSUES_RUNNING_INTERVAL, true);
      } else {
        await commit(SET_ISSUES_RUNNING_INTERVAL, true);
      }

      result = await loadCentralizedIssues(
        {
          sort: payload.sort || getters.centralizedIssuesListOptions.sort,
          limit: payload.limit || getters.centralizedIssuesListOptions.limit,
          page: payload.page || getters.centralizedIssuesListOptions.page,
          toggles,
          refId: payload?.refId || '',
          search: payload.search === undefined ? getters.centralizedIssuesListOptions.search : payload.search,
        },
        { ...payloadFilter },
      );

      if (payload.forDashboard) {
        await commit(SET_DASHBOARD_ISSUES_RUNNING_INTERVAL, false);
      } else if (payload.projectId) {
        await commit(SET_PROJECT_ISSUES_RUNNING_INTERVAL, false);
      } else {
        await commit(SET_ISSUES_RUNNING_INTERVAL, false);
      }

      // If this was a poll (payload.refId set) but the active refId has since changed
      // (user toggled a filter / changed page mid-load), drop the response entirely.
      let activeRefId = getters.issuesRefId;
      if (payload.forDashboard) {
        activeRefId = getters.dashboardIssuesRefId;
      } else if (payload.projectId) {
        activeRefId = getters.projectIssuesRefId;
      }
      if (payload.refId && payload.refId !== activeRefId) {
        stalePoll = true;
        return;
      }

      if (result) {
        if (result?.refId) {
          if (payload.forDashboard) {
            if (result.refId !== getters.dashboardIssuesRefId) {
              commit(CLEAR_CENTRALIZED_ISSUES);
              commit(SET_DASHBOARD_ISSUES_REF_ID, result.refId);
            }
            if (result?.progress) {
              commit(SET_DASHBOARD_ISSUES_PROGRESS, result.progress);
            }
          } else if (payload.projectId) {
            if (result.refId !== getters.projectIssuesRefId) {
              commit(CLEAR_CENTRALIZED_ISSUES);
              commit(SET_PROJECT_ISSUES_REF_ID, result.refId);
            }
            if (result?.progress) {
              commit(SET_PROJECT_ISSUES_PROGRESS, result.progress);
            }
          } else {
            if (result.refId !== getters.issuesRefId) {
              commit(CLEAR_CENTRALIZED_ISSUES);
              commit(SET_ISSUES_REF_ID, result.refId);
            }
            if (result?.progress) {
              commit(SET_ISSUES_PROGRESS, result.progress);
            }
          }
        } else {
          if (payload.forDashboard) {
            commit(CLEAR_DASHBOARD_ISSUES_REF_ID);
            commit(CLEAR_DASHBOARD_ISSUES_PROGRESS);
          } else if (payload.projectId) {
            commit(CLEAR_PROJECT_ISSUES_REF_ID);
            commit(CLEAR_PROJECT_ISSUES_PROGRESS);
          } else {
            await commit(SET_ISSUES_PROGRESS, 100);
            await new Promise(r => setTimeout(r, 900));
            commit(CLEAR_ISSUES_REF_ID);
            commit(CLEAR_ISSUES_PROGRESS);
          }

          const {
            items, page, pageCount, count, total,
          } = result;
          if (payload.forDashboard) {
            commit(SET_CENTRALIZED_ISSUES_FOR_DASHBOARD, items);
          } else if (payload.forProjectDetails) {
            commit(SET_CENTRALIZED_ISSUES_PAGINATION_STATE, {
              page,
              pageCount,
              count,
              total,
            });
            commit(SET_CENTRALIZED_ISSUES_FOR_DASHBOARD, items);
          } else {
            commit(SET_CENTRALIZED_ISSUES_PAGINATION_STATE, {
              page,
              pageCount,
              count,
              total,
            });
            commit(SET_CENTRALIZED_ISSUES, items);
          }
        }
      }
      const updatedRetryCounter = {
        ...getters.retryCounter,
        issues: 0,
      };
      await commit(SET_RETRY_COUNTER, updatedRetryCounter);
    } catch (e) {
      if (getters.retryCounter.issues > 5) {
        if (payload.forDashboard) {
          commit(CLEAR_DASHBOARD_ISSUES_PROGRESS);
          commit(CLEAR_DASHBOARD_ISSUES_REF_ID);
        } else if (payload.projectId) {
          commit(CLEAR_PROJECT_ISSUES_PROGRESS);
          commit(CLEAR_PROJECT_ISSUES_REF_ID);
        } else {
          commit(CLEAR_CENTRALIZED_ISSUES);
          commit(CLEAR_ISSUES_PROGRESS);
          commit(CLEAR_ISSUES_REF_ID);
        }
      } else {
        const updatedRetryCounter = {
          ...getters.retryCounter,
          issues: getters.retryCounter.issues + 1,
        };
        await commit(SET_RETRY_COUNTER, updatedRetryCounter);
      }
    } finally {
      // Don't touch the loading flag for a dropped stale poll - the active request owns it.
      if (!stalePoll && !result?.refId) {
        if (payload.forDashboard) {
          await commit(SET_DASHBOARD_ISSUES_LIST_LOADING, false);
        } else if (payload.projectId) {
          await commit(SET_PROJECT_ISSUES_LIST_LOADING, false);
        } else {
          await commit(SET_ISSUES_LIST_LOADING, false);
        }
      }
    }
  },
};
